import { MutationOptions, useMutation, useQuery, useQueryClient } from "react-query";
import { API_URL, APIError, APIResponse, get, post } from "../api";

export interface ComplianceAddress {
  line_1: string | null;
  line_2: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
}

export interface AddressFieldRules {
  country: string;
  /** False for Hong Kong, the UAE, much of West Africa — hide the field entirely. */
  uses_postcodes: boolean;
  /** False for Nigeria even though NIPOST codes exist, since almost nobody uses them. */
  postcode_required: boolean;
  postcode_example: string | null;
  /**
   * False on the free provider, which cannot list the premises at a postcode —
   * that data is licensed. Show a typed house-number field rather than a picker
   * that would always come back empty.
   */
  supports_postcode_lookup: boolean;
  provider: string;
}

export interface PostcodeLookup {
  postcode: string | null;
  country: string | null;
  city: string | null;
  state: string | null;
  addresses: AddressSuggestion[];
}

export interface AddressSuggestion {
  id: string;
  type: string;
  label: string;
  description: string;
}

/** The address currently on file, for prefilling the form. */
export function useComplianceAddress(enabled = true) {
  const query = useQuery<
    APIResponse<{ address: ComplianceAddress | null; verified: boolean; verified_at: string | null }>,
    APIError
  >({
    queryKey: ["compliance-address"],
    queryFn: () =>
      get(`${API_URL}/account/compliance/address`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
    enabled,
  });

  return {
    address: query.data?.data?.address ?? null,
    isAddressVerified: query.data?.data?.verified ?? false,
    isLoadingAddress: query.isLoading,
  };
}

/**
 * What the form should ask for in a given country.
 *
 * The address form is not the same everywhere. Asking the server rather than
 * hardcoding rules per country is what keeps a Hong Kong customer from being
 * blocked on a postcode field for a country that has no postcodes.
 */
export function useAddressFieldRules(country: string | null | undefined) {
  const query = useQuery<APIResponse<AddressFieldRules>, APIError>({
    queryKey: ["address-field-rules", country],
    queryFn: () =>
      get(`${API_URL}/account/compliance/address/requirements?country=${encodeURIComponent(country ?? "")}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }),
    enabled: Boolean(country),
    staleTime: 10 * 60_000,
  });

  return {
    rules: query.data?.data ?? null,
    isLoadingRules: query.isLoading,
  };
}

/**
 * Validates a postcode and returns what the active provider knows about it.
 *
 * Call with a debounced value — this is a network round trip per keystroke
 * otherwise. `enabled` is gated on a plausible length so an obviously
 * incomplete postcode does not produce an error message while the customer is
 * still typing it.
 */
export function usePostcodeLookup(country: string | null | undefined, postcode: string) {
  const trimmed = postcode.trim();

  const query = useQuery<APIResponse<PostcodeLookup>, APIError>({
    queryKey: ["postcode-lookup", country, trimmed],
    queryFn: () =>
      get(
        `${API_URL}/account/compliance/address/postcode?country=${encodeURIComponent(
          country ?? ""
        )}&postcode=${encodeURIComponent(trimmed)}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      ),
    enabled: Boolean(country) && trimmed.length >= 3,
    retry: false,
    keepPreviousData: false,
  });

  return {
    lookup: query.data?.data ?? null,
    isLookingUpPostcode: query.isFetching,
    // A 400 here means the postcode is malformed, which is a normal result the
    // form shows inline rather than an error state.
    postcodeError: query.isError ? (query.error as APIError)?.message ?? null : null,
  };
}

export function useSaveComplianceAddress(
  options?: MutationOptions<APIResponse<any>, APIError, ComplianceAddress>
) {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (variables: ComplianceAddress) =>
      post(`${API_URL}/account/compliance/address`, variables, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }),
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // Saving an address settles residency server-side, which changes what
        // the checklist and the booking gate say. Refresh both rather than
        // leaving a stale "address missing" on screen.
        queryClient.invalidateQueries("compliance-address");
        queryClient.invalidateQueries("kyc-requirements");
        queryClient.invalidateQueries("geo-resolve");
        options?.onSuccess?.(data, variables, context);
      },
    }
  );

  return {
    saveAddress: mutation.mutate,
    savingAddress: mutation.isLoading,
    saveAddressError: mutation.error,
    isSaveAddressError: mutation.isError,
  };
}

export function useResolveAddressSuggestion() {
  const mutation = useMutation<
    APIResponse<{ address: ComplianceAddress }>,
    APIError,
    { id: string; country: string }
  >((variables) =>
    post(`${API_URL}/account/compliance/address/suggestion`, variables, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
  );

  return {
    resolveSuggestion: mutation.mutateAsync,
    resolvingSuggestion: mutation.isLoading,
  };
}
